import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertTitle,
  Chip,
  colors,
  DialogContent,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import UpdateIcon from '@mui/icons-material/Update';
import { alpha } from '@mui/material/styles';
import {
  LabourPairFieldPlan,
  RoleRateRowPlan,
  EmployeeCostRowPlan,
  ScopedBillingRowPlan,
  BulkUpdateRoleRatesResponse,
  BulkUpdateEmployeeCostRatesResponse,
  BulkUpdateScopedBillingRatesResponse,
} from 'api/labourApi';
import theme from 'theme';
import { currencyType } from 'api/organizationApi';

const formatMoney = (value: number) => {
  const num = Number(value);
  return Number.isFinite(num) ? `${currencyType}${num.toFixed(4)}` : '—';
};

const roleDisplayLabel = (role: string) => (role === 'ProjectManager' ? 'Project Coordinator' : role);

const ROLE_CHIP_COLOR: Record<string, 'error' | 'info' | 'warning' | 'default'> = {
  Administrator: 'error',
  'Project Coordinator': 'info',
  Supervisor: 'warning',
  Technician: 'default',
};

const RoleChip = ({ role }: { role: string }) => (
  <Chip size="small" variant="outlined" label={role} color={ROLE_CHIP_COLOR[role] ?? 'default'} />
);

const DateBadge = ({ dateStatus }: { dateStatus: LabourPairFieldPlan['dateStatus'] }) => {
  if (dateStatus === 'past') {
    return <Chip size="small" label="Past" color="warning" sx={{ ml: 1 }} />;
  }
  if (dateStatus === 'future') {
    return <Chip size="small" label="Future" color="info" sx={{ ml: 1 }} />;
  }
  return null;
};

const DateCell = ({ field }: { field?: LabourPairFieldPlan }) => {
  if (!field) return <TableCell>—</TableCell>;
  return (
    <TableCell>
      {field.effectiveFrom}
      <DateBadge dateStatus={field.dateStatus} />
    </TableCell>
  );
};

const RateCell = ({ field }: { field?: LabourPairFieldPlan }) => {
  if (!field) return <TableCell>—</TableCell>;
  const unchanged = field.oldValue === undefined || field.action === 'unchanged' || field.dateMoved;
  if (unchanged) return <TableCell>{formatMoney(field.newValue)}</TableCell>;
  return (
    <TableCell>
      {formatMoney(field.oldValue!)} →{' '}
      <Typography component="span" variant="body2" sx={{ fontWeight: 700 }}>
        {formatMoney(field.newValue)}
      </Typography>
    </TableCell>
  );
};

const WafhCell = ({ field }: { field?: LabourPairFieldPlan }) => {
  if (!field) return <TableCell>—</TableCell>;
  const unchanged =
    field.oldSecondaryValue === undefined ||
    field.action === 'unchanged' ||
    field.dateMoved ||
    field.oldSecondaryValue === field.newSecondaryValue;
  if (unchanged) return <TableCell>{formatMoney(field.newSecondaryValue)}</TableCell>;
  return (
    <TableCell>
      {formatMoney(field.oldSecondaryValue!)} →{' '}
      <Typography component="span" variant="body2" sx={{ fontWeight: 700 }}>
        {formatMoney(field.newSecondaryValue)}
      </Typography>
    </TableCell>
  );
};

export const TABLE_HEADER_SX = {
  backgroundColor: colors.blue[50],
  '& th': { color: 'common.black', fontWeight: 'bold' },
};

export const BorderedTable = ({ children }: { children: React.ReactNode }) => (
  <TableContainer
    component={Paper}
    elevation={0}
    sx={{ borderRadius: '12px', border: '1px solid', borderColor: colors.grey[300] }}>
    {children}
  </TableContainer>
);

const SECTION_ICONS: Record<'success' | 'info' | 'warning' | 'error', React.ElementType> = {
  success: CheckCircleOutlineIcon,
  info: UpdateIcon,
  warning: ReportProblemOutlinedIcon,
  error: ErrorOutlineIcon,
};

export const Section = ({
  title,
  count,
  severity,
  defaultExpanded,
  children,
}: {
  title: string;
  count: number;
  severity: 'success' | 'info' | 'warning' | 'error';
  defaultExpanded: boolean;
  children: React.ReactNode;
}) => {
  if (count === 0) return null;
  const paletteColor = theme.palette[severity];
  const Icon = SECTION_ICONS[severity];
  return (
    <Accordion defaultExpanded={defaultExpanded} disableGutters sx={{ boxShadow: 'none', '&:before': { display: 'none', borderRadius: '4px' } }}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{ backgroundColor: alpha(paletteColor.main, 0.12) }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Icon fontSize="small" sx={{ color: paletteColor.main }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: paletteColor.dark }}>
            {title} ({count})
          </Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>{children}</AccordionDetails>
    </Accordion>
  );
};

const SummaryAlert = ({
  committed,
  errorCount,
  children,
}: {
  committed: boolean;
  errorCount: number;
  children: React.ReactNode;
}) => (
  <Alert severity={committed ? 'success' : 'info'}>
    <AlertTitle>
      {committed
        ? errorCount > 0
          ? 'Import Completed (Errors Ignored)'
          : 'Import completed'
        : 'Import Preview'}
    </AlertTitle>
    {children}
  </Alert>
);

const IgnoredAndErrorSections = ({
  response,
  labelHeader,
  getIgnoredLabel,
  getErrorLabel,
}: {
  response: { committed: boolean; ignoredCount: number; errorCount: number; ignored: { row: number; reason: string }[]; errors: { row: number; message: string }[] };
  labelHeader: string;
  getIgnoredLabel: (row: { row: number; reason: string }) => string;
  getErrorLabel: (row: { row: number; message: string }) => string;
}) => (
  <>
    <Section
      title={response.committed ? 'Ignored' : 'To Be Ignored'}
      count={response.ignoredCount}
      severity="warning"
      defaultExpanded={false}>
      <BorderedTable>
        <Table size="small">
          <TableHead sx={TABLE_HEADER_SX}>
            <TableRow>
              <TableCell>Row</TableCell>
              <TableCell>{labelHeader}</TableCell>
              <TableCell>Reason</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {response.ignored.map((row, index) => (
              <TableRow key={index}>
                <TableCell>{row.row}</TableCell>
                <TableCell>{getIgnoredLabel(row) || '-'}</TableCell>
                <TableCell>{row.reason}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </BorderedTable>
    </Section>

    <Section title="Errors" count={response.committed ? 0 : response.errorCount} severity="error" defaultExpanded>
      {response.errorCount > 0 && (
        <Alert severity="warning" sx={{ marginBottom: '10px' }}>
          Rows listed under "Errors" won't be created or updated, they are ignored entirely. Fix the
          reasons shown and re-upload to include them, or continue and they'll be left out of this import.
        </Alert>
      )}
      <BorderedTable>
        <Table size="small">
          <TableHead sx={TABLE_HEADER_SX}>
            <TableRow>
              <TableCell>Row</TableCell>
              <TableCell>{labelHeader}</TableCell>
              <TableCell>Message</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {response.errors.map((error, index) => (
              <TableRow key={index}>
                <TableCell>{error.row}</TableCell>
                <TableCell>{getErrorLabel(error) || '-'}</TableCell>
                <TableCell>{error.message}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </BorderedTable>
    </Section>
  </>
);

const RoleRateTable = ({ rows }: { rows: RoleRateRowPlan[] }) => (
  <BorderedTable>
    <Table size="small">
      <TableHead sx={TABLE_HEADER_SX}>
        <TableRow>
          <TableCell>Role</TableCell>
          <TableCell>Cost Rate</TableCell>
          <TableCell>WAFH Cost Rate</TableCell>
          <TableCell>Cost Effective Date</TableCell>
          <TableCell>Billing Rate</TableCell>
          <TableCell>WAFH Billing Rate</TableCell>
          <TableCell>Billing Effective Date</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={`${row.row}-${row.role}`}>
            <TableCell><RoleChip role={roleDisplayLabel(row.role)} /></TableCell>
            <RateCell field={row.cost} />
            <WafhCell field={row.cost} />
            <DateCell field={row.cost} />
            <RateCell field={row.billing} />
            <WafhCell field={row.billing} />
            <DateCell field={row.billing} />
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </BorderedTable>
);

export const RoleRatesResultView = ({ response }: { response: BulkUpdateRoleRatesResponse }) => {
  return (
    <DialogContent sx={{ p: 0 }}>
      <Stack direction="column" spacing={2} sx={{ p: 2 }}>
        <SummaryAlert committed={response.committed} errorCount={response.errorCount}>
          Out of {response.totalRows} row(s) you provided, {response.createdCount} role rate(s){' '}
          {response.committed ? 'were created' : 'will be created'}, {response.updatedCount}{' '}
          {response.committed ? 'were updated' : 'will be updated'}, {response.ignoredCount} left
          unchanged, and we found {response.errorCount} error(s).
        </SummaryAlert>

        <Section
          title={response.committed ? 'Created' : 'To Be Created'}
          count={response.createdCount}
          severity="success"
          defaultExpanded={
            response.committed || (response.errorCount === 0 && response.updatedCount === 0)
          }>
          <RoleRateTable rows={response.created} />
        </Section>

        <Section
          title={response.committed ? 'Updated' : 'To Be Updated'}
          count={response.updatedCount}
          severity="info"
          defaultExpanded={response.committed || response.errorCount === 0}>
          <RoleRateTable rows={response.updated} />
        </Section>

        <IgnoredAndErrorSections
          response={response}
          labelHeader="Role"
          getIgnoredLabel={(row) => roleDisplayLabel((row as any).role ?? '')}
          getErrorLabel={(row) => roleDisplayLabel((row as any).role ?? '')}
        />
      </Stack>
    </DialogContent>
  );
};

const EmployeeCostTable = ({ rows }: { rows: EmployeeCostRowPlan[] }) => (
  <BorderedTable>
    <Table size="small">
      <TableHead sx={TABLE_HEADER_SX}>
        <TableRow>
          <TableCell>Employee</TableCell>
          <TableCell>Role</TableCell>
          <TableCell>Cost Rate</TableCell>
          <TableCell>WAFH Cost Rate</TableCell>
          <TableCell>Effective Date</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={`${row.row}-${row.userId}`}>
            <TableCell>{row.employeeName}</TableCell>
            <TableCell><RoleChip role={row.role} /></TableCell>
            <RateCell field={row.cost} />
            <WafhCell field={row.cost} />
            <DateCell field={row.cost} />
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </BorderedTable>
);

export const EmployeeCostResultView = ({ response }: { response: BulkUpdateEmployeeCostRatesResponse }) => {
  return (
    <DialogContent sx={{ p: 0 }}>
      <Stack direction="column" spacing={2} sx={{ p: 2 }}>
        <SummaryAlert committed={response.committed} errorCount={response.errorCount}>
          Out of {response.totalRows} row(s) you provided, {response.createdCount} employee cost rate(s){' '}
          {response.committed ? 'were created' : 'will be created'}, {response.updatedCount}{' '}
          {response.committed ? 'were updated' : 'will be updated'}, {response.ignoredCount}{' '}
          {response.committed ? 'were ignored' : 'will be ignored'}, and we found {response.errorCount} error(s).
        </SummaryAlert>

        <Section
          title={response.committed ? 'Created' : 'To Be Created'}
          count={response.createdCount}
          severity="success"
          defaultExpanded={
            response.committed || (response.errorCount === 0 && response.updatedCount === 0)
          }>
          <EmployeeCostTable rows={response.created} />
        </Section>

        <Section
          title={response.committed ? 'Updated' : 'To Be Updated'}
          count={response.updatedCount}
          severity="info"
          defaultExpanded={response.committed || response.errorCount === 0}>
          <EmployeeCostTable rows={response.updated} />
        </Section>

        <IgnoredAndErrorSections
          response={response}
          labelHeader="Employee"
          getIgnoredLabel={(row) => (row as any).employeeName ?? ''}
          getErrorLabel={(row) => (row as any).employeeName ?? ''}
        />
      </Stack>
    </DialogContent>
  );
};

const ScopedBillingTable = ({ rows }: { rows: ScopedBillingRowPlan[] }) => (
  <BorderedTable>
    <Table size="small">
      <TableHead sx={TABLE_HEADER_SX}>
        <TableRow>
          <TableCell>Role</TableCell>
          <TableCell>Billing Rate</TableCell>
          <TableCell>WAFH Billing Rate</TableCell>
          <TableCell>Effective Date</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={`${row.row}-${row.role}`}>
            <TableCell><RoleChip role={roleDisplayLabel(row.role)} /></TableCell>
            <RateCell field={row.billing} />
            <WafhCell field={row.billing} />
            <DateCell field={row.billing} />
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </BorderedTable>
);

export const ScopedBillingResultView = ({ response }: { response: BulkUpdateScopedBillingRatesResponse }) => {
  return (
    <DialogContent sx={{ p: 0 }}>
      <Stack direction="column" spacing={2} sx={{ p: 2 }}>
        <SummaryAlert committed={response.committed} errorCount={response.errorCount}>
          Out of {response.totalRows} row(s) you provided, {response.createdCount} billing rate(s){' '}
          {response.committed ? 'were created' : 'will be created'}, {response.updatedCount}{' '}
          {response.committed ? 'were updated' : 'will be updated'}, {response.ignoredCount} left
          unchanged, and we found {response.errorCount} error(s).
        </SummaryAlert>

        <Section
          title={response.committed ? 'Created' : 'To Be Created'}
          count={response.createdCount}
          severity="success"
          defaultExpanded={
            response.committed || (response.errorCount === 0 && response.updatedCount === 0)
          }>
          <ScopedBillingTable rows={response.created} />
        </Section>

        <Section
          title={response.committed ? 'Updated' : 'To Be Updated'}
          count={response.updatedCount}
          severity="info"
          defaultExpanded={response.committed || response.errorCount === 0}>
          <ScopedBillingTable rows={response.updated} />
        </Section>

        <IgnoredAndErrorSections
          response={response}
          labelHeader="Role"
          getIgnoredLabel={(row) => roleDisplayLabel((row as any).role ?? '')}
          getErrorLabel={(row) => roleDisplayLabel((row as any).role ?? '')}
        />
      </Stack>
    </DialogContent>
  );
};
